import React, { FC, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import orderBy from 'lodash/orderBy';
import moment from 'moment';

import { IPayPeriodWeekDTO } from 'src/api/dtos/payPeriodWeek.dto';
import { useTimesheetQuery } from 'src/features/timesheets/providers';
import { ChevronLeftIcon, ChevronRightIcon, IconButton } from 'src/shared/components';
import { DATE_MONTH_FORMAT } from 'src/shared/constants';
import { WeekTypeEnums } from 'src/shared/enums';
import { TranslationFunction, useTranslate } from 'src/shared/providers';

const DEFAULT_VISIBLE_COUNT = 10;

export const TimesheetPayPeriodWeekSelector: FC = () => {
  const { t } = useTranslate();

  const { selectedPayPeriodWeek, payPeriodWeeks, onPayPeriodWeekChange } = useTimesheetQuery();

  const [loadMore, setLoadMore] = useState(false);
  const [menuEl, setMenuEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(menuEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuEl(event.currentTarget);
  };

  const handleClose = () => {
    setMenuEl(null);
  };

  const orderedOptions = useMemo(
    () => orderBy(payPeriodWeeks, ['startDate', 'endDate'], ['desc', 'desc']),
    [payPeriodWeeks],
  );

  const selectedIndex = useMemo(
    () =>
      orderedOptions.findIndex(
        (option) =>
          option.startDate === selectedPayPeriodWeek?.startDate && option.endDate === selectedPayPeriodWeek?.endDate,
      ),
    [orderedOptions, selectedPayPeriodWeek],
  );

  const handleQuickNavigation = (direction: -1 | 1) => {
    if (selectedIndex < 0) {
      return;
    }

    if (selectedIndex <= 0 && direction === -1) {
      return;
    }

    if (selectedIndex >= orderedOptions.length - 1 && direction === 1) {
      return;
    }

    const newSelected = orderedOptions[selectedIndex + direction];

    if (!newSelected) {
      return;
    }

    onPayPeriodWeekChange(newSelected);
  };

  return (
    <>
      <Stack direction="row" gap={1}>
        <IconButton
          onClick={() => handleQuickNavigation(1)}
          disabled={!selectedPayPeriodWeek || selectedIndex >= orderedOptions.length - 1}
        >
          <ChevronLeftIcon size={20} />
        </IconButton>
        <Button color="secondary" onClick={handleOpen} disabled={!selectedPayPeriodWeek} sx={{ minWidth: 210 }}>
          {selectedPayPeriodWeek ? (
            formatPayPeriodWeek(selectedPayPeriodWeek, t)
          ) : (
            <Skeleton className="rounded" variant="rectangular" animation="wave" height="16px" width={160} />
          )}
        </Button>
        <IconButton onClick={() => handleQuickNavigation(-1)} disabled={!selectedPayPeriodWeek || selectedIndex <= 0}>
          <ChevronRightIcon size={20} />
        </IconButton>
      </Stack>
      <Menu anchorEl={menuEl} open={openMenu} onClose={handleClose}>
        <Stack padding="0 8px" minWidth={160} overflow="hidden">
          <Typography fontSize="0.875rem" fontWeight={600} sx={{ p: 1 }}>
            {t('timesheets.selectPeriod')}
          </Typography>
          <Stack maxHeight={400} overflow="auto">
            {orderedOptions
              .filter((payPeriodWeek) => payPeriodWeek.weekType !== WeekTypeEnums.NEXT)
              .slice(0, loadMore ? undefined : DEFAULT_VISIBLE_COUNT)
              .map((payPeriodWeek) => (
                <MenuItem
                  key={`pay-period-week-${payPeriodWeek.startDate}-${payPeriodWeek.endDate}`}
                  selected={
                    payPeriodWeek.startDate === selectedPayPeriodWeek?.startDate &&
                    payPeriodWeek.endDate === selectedPayPeriodWeek?.endDate
                  }
                  onClick={() => onPayPeriodWeekChange(payPeriodWeek)}
                  sx={{ alignSelf: 'flex-start', p: 1, fontSize: '0.875rem', width: '100%' }}
                >
                  {formatPayPeriodWeek(payPeriodWeek, t)}
                </MenuItem>
              ))}
          </Stack>
          {payPeriodWeeks.length > DEFAULT_VISIBLE_COUNT && (
            <>
              <Divider sx={{ m: '0 !important' }} />
              <Button
                size="small"
                variant="text"
                onClick={() => setLoadMore(!loadMore)}
                sx={{ minHeight: 'initial', p: 1, alignSelf: 'flex-start' }}
              >
                {loadMore ? t('timesheets.showLessPeriods') : t('timesheets.showMorePeriods')}
              </Button>
            </>
          )}
        </Stack>
      </Menu>
    </>
  );
};

const formatPayPeriodWeek = (payPeriodWeek: IPayPeriodWeekDTO, t: TranslationFunction) => {
  const { startDate, endDate, weekType } = payPeriodWeek;

  return `${moment(startDate).format(DATE_MONTH_FORMAT)} - ${moment(endDate).format(DATE_MONTH_FORMAT)}
  ${weekType === WeekTypeEnums.PREVIOUS ? ` (${t('global.previous')})` : ''}
  ${weekType === WeekTypeEnums.CURRENT ? ` (${t('global.current')})` : ''}
  ${weekType === WeekTypeEnums.NEXT ? ` (${t('global.next')})` : ''}
  `;
};
